import { IResultMessage, ResultMessageType } from "@multiweb/types/contracts";
import { ArrayUtils } from "../../../../Global/Base/Utils/ArrayUtils";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IContentBuilder } from "./IContentBuilder";

/**
 * @internal
 */
export class UserMsgsBuilder implements IContentBuilder {
    private _parent: HTMLElement;
    private _data: IUserMsgsData[];

    constructor(msgs: IUserMsgsData[]) {
        this._data = msgs;
    }

    private async _loadData(msgs: IUserMsgsData[]): Promise<void> {
        this._parent = createElement({ classes: "master-group-message" });
        if (msgs.length == 1) {
            const msg = msgs[0];
            const msgGroup = new UserMsgGroup("");
            const oneMsg = createElement({ classes: "one-message" });

            for (const item of msg.messages) {
                msgGroup.addItem(item);
            }
            oneMsg.innerHTML = msgGroup.contentElement.innerHTML;
            this._parent.appendChild(oneMsg);
        } else {
            const savedGroup = new UserMsgGroup(await Localization
                .getTextPromise("GUI-616403")); // Informace o uložených záznamech
            const unsavedGroup = new UserMsgGroup(await Localization
                .getTextPromise("GUI-616405")); // Některé záznamy jsme nemohli uložit

            for (const m of msgs) {
                const mgroup = new UserMsgGroup(m.objectIdentification);
                let success = true;
                for (const mchild of m.messages) {
                    mgroup.addItem(mchild);
                    if (mchild.type == ResultMessageType.error || mchild.type == ResultMessageType.recommendation)
                        success = false;
                }

                if (success)
                    savedGroup.addItem(mgroup);
                else
                    unsavedGroup.addItem(mgroup);
            }

            savedGroup.name += ` (${savedGroup.itemCount})`;
            unsavedGroup.name += ` (${unsavedGroup.itemCount})`;
            unsavedGroup.isClosed = false;

            this._parent.appendChild(savedGroup.element);
            this._parent.appendChild(unsavedGroup.element);
        }
    }

    public async build(): Promise<HTMLElement> {
        await this._loadData(this._data);
        return this._parent;
    }
}

class UserMsgGroup {
    private _contentElement: HTMLElement;
    private _nameElement: HTMLElement;
    private _itemCount: number = 0;
    private _items: IUserMsgItem[] = [];

    public get itemCount(): number {
        return this._itemCount;
    }

    private _element: HTMLElement;

    public get element(): HTMLElement {
        return this._element;
    }

    public get contentElement(): HTMLElement {
        return this._contentElement;
    }

    private _name: string = "";

    public get name(): string {
        return this._name;
    }

    public set name(v: string) {
        this._name = v;
        this._nameElement.textContent = v;
    }

    private _isClosed: boolean = true;

    public get isClosed(): boolean {
        return this._isClosed;
    }

    /**
     * Určuje zda je skupina v GUI zavřená a nebo otevřena
     */
    public set isClosed(v: boolean) {
        this._isClosed = v;
        this._element.classList.toggle("closed", v);
    }

    constructor(text: string) {
        this._element = createElement({ classes: "msg-group closed hidden" });
        const topElement = createElement({ classes: "inner-group top" });
        this._contentElement = createElement({ classes: "inner-group content" });

        const arrow = createElement({ classes: "arrow" });
        this._nameElement = createElement({ classes: "name", html: StringUtils.stringToHTMLText(text) });

        this.name = text;

        topElement.appendChild(arrow);
        topElement.appendChild(this._nameElement);

        this._element.appendChild(topElement);
        this._element.appendChild(this._contentElement);

        topElement.addEventListener("click",
            () => {
                this.isClosed = !this.isClosed;
            });
    }

    public addItem(item: UserMsgGroup | IUserMsgItem): void {
        this._element.classList.remove("hidden");
        if (item instanceof UserMsgGroup) {
            this._contentElement.appendChild(item.element);
        } else {
            if (this._itemCount > 0) {
                const lastMessage = this._items[this._itemCount - 1];

                // Existují situace, kdy pro více záznamů se vrací více hlášek, kde jedna je vždy stejná. V tomto případě je potřeba je zobrazovat všechny.
                // Proto se zavedlo porovnávání s poslední hláškou místo kontroly, jestli daná hláška už v seznamu existuje.
                if (lastMessage.message === item.message && lastMessage.type === item.type) {
                    // projiti seznamu atributu
                    if (lastMessage.additionalParams && item.additionalParams) {
                        lastMessage.additionalParams = ArrayUtils.getUniqueArray(lastMessage.additionalParams.concat(item.additionalParams));
                    } else if (item.additionalParams) {
                        lastMessage.additionalParams = item.additionalParams;
                    } else if (lastMessage.additionalParams) {
                        // Prazdny if
                    } else {
                        item.additionalParams = [];
                    }
                    this._redraw();
                    return;
                }
            }

            let text = item.message ? StringUtils.stringToHTMLText(item.message) : "";
            if (item.additionalParams && item.additionalParams.length > 0)
                text += ` (${item.additionalParams.join(", ")})`; //`

            this._contentElement.appendChild(
                createElement({
                    classes: ["user-msg-item", ResultMessageType[item.type].toLowerCase()],
                    html: text,
                }));

            this._itemCount++;
            this._items.push(item);
        }
    }

    private _redraw(): void {
        this._contentElement.innerHTML = "";
        for (const item of this._items) {
            let text = StringUtils.stringToHTMLText(item.message);
            if (item.additionalParams && item.additionalParams.length > 0)
                text += ` (${item.additionalParams.join(", ")})`; //`

            this._contentElement.appendChild(
                createElement({
                    classes: ["user-msg-item", ResultMessageType[item.type].toLowerCase()],
                    html: text,
                }));
        }
    }
}

export interface IUserMsgsData {
    objectIdentification: string;
    messages: IUserMsgItem[];
}

export interface IUserMsgItem {
    message: string;
    type: ResultMessageType;
    additionalParams?: string[];
}

export class UserMsgsData implements IUserMsgsData {
    public readonly objectIdentification: string | undefined;
    public readonly messages: IUserMsgItem[];

    constructor(resultMessages: readonly IResultMessage[], objectIdentification: string | undefined = undefined) {
        this.objectIdentification = objectIdentification;
        this.messages = [];

        if (!resultMessages)
            return;

        for (const message of resultMessages) {
            const msg: IUserMsgItem = { message: message.text, type: message.type };

            if (msg.type === ResultMessageType.error && message.locKey)
                msg.additionalParams = [message.locKey]; // u errorů se má zobrazovat i kód chyby

            this.messages.push(msg);
        }
    }
}
